import * as Sentry from "@sentry/react";
import { redactScreenTokensDeep } from "./screenPrivacy";

export const initSentry = () => {
    const dsn = import.meta.env.VITE_SENTRY_DSN;

    if (!dsn) {
        console.warn("Sentry DSN not found. Sentry will not be initialized.");
        return;
    }

    // Vercel builds every branch in Vite mode "production", so MODE cannot
    // tell staging from live. VITE_APP_ENV is set per Vercel branch instead;
    // alert rules filter on it so a staging test never pages anyone.
    const environment = import.meta.env.VITE_APP_ENV ?? "development";
    const isProduction = environment === "production";

    Sentry.init({
        dsn,
        environment,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
        ],
        enableLogs: true,
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", /^https:\/\/.*\.supabase\.co/],
        // Session Replay
        // Free tier = 50 replays a month. In production every one of them is
        // reserved for an error or a fan report (which flushes the buffered
        // session on demand); random 10% sampling would burn the quota on
        // sessions nobody will ever watch.
        replaysSessionSampleRate: isProduction ? 0 : 0.1,
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        // Stadium-screen URLs carry the club's screen token (request URL,
        // transaction name, navigation breadcrumbs): scrub it before it leaves.
        beforeSend: (event) => redactScreenTokensDeep(event),
        beforeSendTransaction: (event) => redactScreenTokensDeep(event),
    });
};

/**
 * Attach the signed-in user to every subsequent error.
 *
 * Without this every Sentry issue reads "Users: 0" and an incident can only be
 * pinned to a person by matching timestamps and device strings by hand — which
 * is exactly what the 31 Aug 2026 auth-lock incident cost us.
 */
export const setSentryUser = (user: { id: string; email?: string | null }) => {
    Sentry.setUser({ id: user.id, email: user.email ?? undefined });
};

/**
 * Fan pages have no account, so identity is the seat. Sent as context rather
 * than as a user so anonymous fans are never counted as identified people in
 * Sentry's user metrics.
 */
export const setSentryFanContext = (ctx: {
    tagId?: string | null;
    seatId?: string | null;
    clubId?: string | null;
    stadiumSlug?: string | null;
}) => {
    Sentry.setContext("fan_seat", {
        tag_id: ctx.tagId ?? null,
        seat_id: ctx.seatId ?? null,
        club_id: ctx.clubId ?? null,
        stadium: ctx.stadiumSlug ?? null,
    });
    // Tags are filterable in the Sentry UI; context is not.
    if (ctx.clubId) Sentry.setTag("club_id", ctx.clubId);
    if (ctx.stadiumSlug) Sentry.setTag("stadium", ctx.stadiumSlug);
};

export const clearSentryUser = () => {
    Sentry.setUser(null);
};
